import { useCallback, useEffect, useState } from 'react';
import { Screen } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, EmptyState, ListRow, useConfirm } from '@beyond-js/ui/react';
import { Outage, Panel, Problem, useLast, useQuestions } from '@accounts/ui';

export interface Token {
	id: string;
	name: string;
	scopes: string[];
	organization?: string;
	expires: string;
	created: string;
	used?: string;
}

/**
 * Personal access tokens (E8, ACC-21): credentials for automation such as a CI job, each limited to the
 * scopes it is given (`<product>:<scope>`, as each product documents them), optionally to one
 * organization, and to a lifetime. This page is the collection: the person's tokens, each with Revoke in
 * its row's menu, and **New token** as the header's action, which opens creating one on its own page
 * (`TokenCreation`), so viewing them is never led by a form. Revoking stops a token at once.
 *
 * When Accounts does not answer the list, the page says so once (`Outage`, D163) and the list says it in
 * its place, or over the tokens kept from the last read, when that was. A revocation keeps its own answer.
 */
export function Tokens() {
	const { text, date, client, identity, renewed, language } = usePresence();
	// The change sent last, which the person may send again when it was not made (D186)
	const last = useLast();
	const { confirm } = useConfirm(useQuestions());
	const [tokens, setTokens] = useState<Token[]>();
	// When the list was last read, and why it could not be read since; a failed change is `failure`
	const [at, setAt] = useState<number>();
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(
		() =>
			Refusal.answered(async () => {
				setTokens((await client.get<{ tokens: Token[] }>('/v1/account/tokens')).tokens);
				setAt(Date.now());
			}, setUnread),
		[client]
	);
	useEffect(() => {
		void read();
	}, [read, renewed]);

	async function revoke(token: Token) {
		const accepted = await confirm({
			title: text('tokens.revoke.title', { name: token.name }),
			message: text('tokens.revoke.body'),
			accept: text('tokens.revoke'),
			cancel: text('action.cancel'),
			tone: 'danger'
		});
		if (!accepted) return;
		await end(token);
	}

	async function end(token: Token) {
		last.keep(() => end(token));
		setFailure(undefined);
		try {
			await client.delete(`/v1/account/tokens/${encodeURIComponent(token.id)}`, { replay: false });
			await read();
		} catch (error) {
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			if (Refusal.is(error) && error.unknown) await read();
		}
	}

	const named = (id?: string) => identity?.organizations.find(entry => entry.organization === id)?.name ?? id;
	const outage = Boolean(unread?.unreachable);
	return (
		<Screen template="list" width="fluid" title={text('nav.tokens')} actions={<Button variant="primary" href="/tokens/new" label={text('tokens.new')} />}>
			<Outage reads={[{ key: 'tokens', title: text('tokens.title'), unread, run: read }]} />
			{failure ? <Problem error={failure} again={last.kept ? last.again : undefined} check={read} /> : null}
			{unread && !outage ? <Problem error={unread} /> : null}

			<Panel title={text('tokens.title')} waiting={!tokens && !unread} unavailable={outage && !tokens} updated={outage && tokens ? (at ?? null) : undefined}>
				{tokens && !tokens.length ? <EmptyState kind="empty" title={text('tokens.none')} text={text('tokens.none.lead')} labels={EmptyState.labels[language]} /> : null}
				{tokens?.length ? (
					<ul className="bui-rows" data-framed aria-label={text('tokens.title')}>
						{tokens.map(token => (
							<ListRow
								key={token.id}
								title={token.name}
								meta={[
									token.scopes.join(' '),
									token.organization ? named(token.organization) : text('tokens.organization.every'),
									text('tokens.expires', { date: date(token.expires) }),
									token.used ? text('tokens.used', { date: date(token.used) }) : null
								]}
								menu={[{ label: text('tokens.revoke'), tone: 'danger', onSelect: () => void revoke(token) }]}
							/>
						))}
					</ul>
				) : null}
			</Panel>
		</Screen>
	);
}
